import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  LocationOn,
  Business,
  Layers,
  Home,
  MeetingRoom,
  Apartment,
  Room as RoomIcon,
  ExpandMore,
  ChevronRight,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import { useTheme } from '@mui/material/styles';

// Location type icons mapping
const LOCATION_TYPE_ICONS = {
  1: LocationOn,  // Site
  2: Business,    // Building
  3: Layers,      // Floor
  4: Home,        // Unit
  5: MeetingRoom, // Room
  6: Apartment,   // Zone
  7: RoomIcon,    // Area
  8: LocationOn   // Other
};

// Location type labels
const LOCATION_TYPE_LABELS = {
  1: 'Site',
  2: 'Building',
  3: 'Floor',
  4: 'Unit',
  5: 'Room',
  6: 'Zone',
  7: 'Area',
  8: 'Other'
};

// Location type colors
const LOCATION_TYPE_COLORS = {
  1: '#2196f3', // Blue - Site
  2: '#4caf50', // Green - Building
  3: '#ff9800', // Orange - Floor
  4: '#9c27b0', // Purple - Unit
  5: '#f44336', // Red - Room
  6: '#00bcd4', // Cyan - Zone
  7: '#ffeb3b', // Yellow - Area
  8: '#607d8b'  // Grey - Other
};

const FAVORITE_LOCATIONS_KEY = 'caro_favorite_locations';

/**
 * LocationTreeView Component
 *
 * Displays locations in a hierarchical tree structure with:
 * - Visual hierarchy with icons and colors
 * - Search/filter functionality
 * - Favorites management
 * - Expand/collapse all
 */
const LocationTreeView = ({
  locations = [],
  value = null,
  onChange,
  loading = false,
  disabled = false,
  required = false,
  label = "Location",
  helperText = "",
  error = null,
  showFavorites = true,
  autoExpandFavorites = true,
  autoExpandSelected = true,
  maxHeight = 400
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);

  // Load favorites from localStorage
  useEffect(() => {
    if (showFavorites) {
      const stored = localStorage.getItem(FAVORITE_LOCATIONS_KEY);
      if (stored) {
        try {
          setFavorites(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to load favorite locations:', e);
        }
      }
    }
  }, [showFavorites]);

  // Build location hierarchy
  const locationTree = useMemo(() => {
    const buildTree = (parentId = null) => {
      return locations
        .filter(loc => {
          const locParentId = loc.parentId || loc.parent_id;
          return parentId === null ? !locParentId : locParentId === parentId;
        })
        .map(location => ({
          ...location,
          children: buildTree(location.id || location._id || location.location_UUID)
        }))
        .sort((a, b) => {
          // Sort by location type first, then by name
          if (a.locationType !== b.locationType) {
            return (a.locationType || 999) - (b.locationType || 999);
          }
          const aName = a.friendlyName || a.name || '';
          const bName = b.friendlyName || b.name || '';
          return aName.localeCompare(bName);
        });
    };

    return buildTree();
  }, [locations]);

  // Filter tree based on search term with flexible pattern matching
  const filterTree = (nodes, term) => {
    if (!term) return nodes;

    const searchLower = term.toLowerCase().trim();

    // Split search term by spaces to handle multiple search parts
    // Example: "SH19 4-02" becomes ["sh19", "4-02"]
    const searchParts = searchLower.split(/\s+/).filter(part => part.length > 0);

    // Function to check if a text matches all search parts
    const matchesAllParts = (text) => {
      const textLower = text.toLowerCase();

      // All search parts must be present in the text
      return searchParts.every(part => {
        // Remove leading zeros and hyphens for flexible matching
        // "402" should match "4-02" or "04-02"
        const normalizedPart = part.replace(/^0+/, '').replace(/-/g, '');
        const normalizedText = textLower.replace(/^0+/, '').replace(/-/g, '');

        // Check if the part exists in the normalized text
        if (normalizedText.includes(normalizedPart)) {
          return true;
        }

        // Also check original text for exact matches
        if (textLower.includes(part)) {
          return true;
        }

        return false;
      });
    };

    return nodes.reduce((acc, node) => {
      const nodeName = (node.friendlyName || node.name || '');
      const nodeType = LOCATION_TYPE_LABELS[node.locationType] || '';

      // Check if node matches search criteria
      const matchesSearch = matchesAllParts(nodeName) ||
                           matchesAllParts(nodeType);

      const filteredChildren = filterTree(node.children || [], term);

      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  };

  const filteredTree = useMemo(() => {
    return filterTree(locationTree, searchTerm);
  }, [locationTree, searchTerm]);

  // Auto-expand search results for better visibility
  useEffect(() => {
    if (searchTerm && filteredTree.length > 0) {
      // Auto-expand all nodes when searching for better visibility
      const getAllNodeIds = (nodes) => {
        return nodes.reduce((acc, node) => {
          const nodeId = String(node.id || node._id || node.location_UUID);
          acc.push(nodeId);
          if (node.children?.length > 0) {
            acc.push(...getAllNodeIds(node.children));
          }
          return acc;
        }, []);
      };

      setExpandedItems(getAllNodeIds(filteredTree));
    }
  }, [searchTerm, filteredTree]);

  // Highlight matching text in search results
  const highlightMatch = (text, search) => {
    if (!search) return text;

    const searchParts = search.toLowerCase().trim().split(/\s+/).filter(part => part.length > 0);

    // Create a regex pattern that matches any of the search parts
    const pattern = searchParts.map(part => {
      // Escape special regex characters
      const escapedPart = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return escapedPart;
    }).join('|');

    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      const isMatch = searchParts.some(searchPart =>
        part.toLowerCase() === searchPart.toLowerCase()
      );

      return isMatch ? (
        <Box
          component="span"
          key={index}
          sx={{
            backgroundColor: alpha(theme.palette.warning.main, 0.3),
            fontWeight: 600,
            borderRadius: 0.5,
            px: 0.25
          }}
        >
          {part}
        </Box>
      ) : (
        part
      );
    });
  };

  // Auto-expand selected location and its parents
  useEffect(() => {
    if (value && autoExpandSelected) {
      const expandParents = (locId, locs) => {
        const expanded = [];
        let current = locs.find(l =>
          (l.id || l._id || l.location_UUID) === locId
        );

        while (current) {
          const currentId = current.id || current._id || current.location_UUID;
          expanded.push(String(currentId));

          if (current.parentId || current.parent_id) {
            current = locs.find(l =>
              (l.id || l._id || l.location_UUID) === (current.parentId || current.parent_id)
            );
          } else {
            break;
          }
        }

        return expanded;
      };

      const valueId = value.id || value._id || value.location_UUID;
      const toExpand = expandParents(valueId, locations);
      setExpandedItems(prev => [...new Set([...prev, ...toExpand])]);
      setSelectedItemId(String(valueId));
      console.log('[LocationTreeView] Value updated:', { value, valueId, selectedItemId: String(valueId) });
    } else if (!value) {
      setSelectedItemId(null);
      console.log('[LocationTreeView] Value cleared');
    }
  }, [value, locations, autoExpandSelected]);

  // Auto-expand favorites
  useEffect(() => {
    if (autoExpandFavorites && favorites.length > 0) {
      const favoriteIds = favorites.map(String);
      setExpandedItems(prev => [...new Set([...prev, ...favoriteIds])]);
    }
  }, [favorites, autoExpandFavorites]);

  // Toggle favorite
  const toggleFavorite = (location, event) => {
    event.stopPropagation();
    const id = location.id || location._id || location.location_UUID;
    const updated = favorites.includes(id)
      ? favorites.filter(fid => fid !== id)
      : [...favorites, id];

    setFavorites(updated);
    localStorage.setItem(FAVORITE_LOCATIONS_KEY, JSON.stringify(updated));
  };

  // Get location icon
  const getLocationIcon = (location) => {
    const IconComponent = LOCATION_TYPE_ICONS[location.locationType] || LocationOn;
    const color = LOCATION_TYPE_COLORS[location.locationType] || '#607d8b';
    return <IconComponent sx={{ fontSize: 18, color }} />;
  };

  // Handle item selection
  const handleItemSelectionToggle = (event, itemId) => {
    console.log('[LocationTreeView] Selection triggered:', { itemId, event });
    if (!itemId) return;

    const location = locations.find(l =>
      String(l.id || l._id || l.location_UUID) === String(itemId)
    );

    console.log('[LocationTreeView] Found location:', location);

    if (location && onChange) {
      setSelectedItemId(String(itemId));
      onChange(event, location);
      console.log('[LocationTreeView] onChange called with location:', location);
    }
  };

  // Expand/Collapse all
  const handleExpandAll = () => {
    const allIds = locations.map(l => String(l.id || l._id || l.location_UUID));
    setExpandedItems(allIds);
  };

  const handleCollapseAll = () => {
    setExpandedItems([]);
  };

  // Render tree item
  const renderTreeItem = (node) => {
    const nodeId = String(node.id || node._id || node.location_UUID);
    const isFavorite = favorites.includes(node.id || node._id || node.location_UUID);
    const displayName = node.friendlyName || node.name || `Location ${nodeId}`;
    const typeLabel = LOCATION_TYPE_LABELS[node.locationType] || 'Location';

    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              pr: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
            onClick={(e) => {
              // Only handle clicks on the main label area, not on buttons
              if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
                console.log('[LocationTreeView] Label clicked for nodeId:', nodeId);
                handleItemSelectionToggle(e, nodeId);
              }
            }}
          >
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {getLocationIcon(node)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {highlightMatch(displayName, searchTerm)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {highlightMatch(typeLabel, searchTerm)}
              </Typography>
            </Box>
            {showFavorites && (
              <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
                <IconButton
                  size="small"
                  onClick={(e) => toggleFavorite(node, e)}
                  sx={{
                    ml: 1,
                    color: isFavorite ? 'warning.main' : 'text.disabled',
                    '&:hover': {
                      color: 'warning.main'
                    }
                  }}
                >
                  {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
      >
        {node.children && node.children.length > 0 && node.children.map(renderTreeItem)}
      </TreeItem>
    );
  };

  return (
    <Box>
      {/* Label */}
      {label && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
        </Typography>
      )}

      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search locations..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled || loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{ mb: 1 }}
      />

      {/* Expand/Collapse controls */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label="Expand All"
          onClick={handleExpandAll}
          disabled={disabled || loading}
          icon={<ExpandMore />}
        />
        <Chip
          size="small"
          label="Collapse All"
          onClick={handleCollapseAll}
          disabled={disabled || loading}
          icon={<ChevronRight />}
        />
        {value && (
          <Chip
            size="small"
            label={value.friendlyName || value.name || 'Selected'}
            onDelete={() => onChange?.(null, null)}
            color="primary"
            icon={getLocationIcon(value)}
          />
        )}
      </Box>

      {/* Tree view */}
      <Paper
        variant="outlined"
        sx={{
          maxHeight,
          overflow: 'auto',
          p: 1,
          backgroundColor: disabled ? alpha(theme.palette.action.disabled, 0.05) : 'background.paper'
        }}
      >
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading locations...
            </Typography>
          </Box>
        ) : filteredTree.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? `No locations found for "${searchTerm}"` : 'No locations available'}
            </Typography>
          </Box>
        ) : (
          <TreeView
            expanded={expandedItems}
            onNodeToggle={(event, itemIds) => setExpandedItems(itemIds)}
            selected={selectedItemId ? [selectedItemId] : []}
            onNodeSelect={(event, itemIds) => {
              console.log('[LocationTreeView] onNodeSelect called:', { event, itemIds });
              // itemIds can be an array or a string depending on MUI version
              const itemId = Array.isArray(itemIds) ? itemIds[0] : itemIds;
              if (itemId) {
                handleItemSelectionToggle(event, itemId);
              }
            }}
            defaultCollapseIcon={<ExpandMore />}
            defaultExpandIcon={<ChevronRight />}
          >
            {filteredTree.map(renderTreeItem)}
          </TreeView>
        )}
      </Paper>

      {/* Helper text or error */}
      {(helperText || error) && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default LocationTreeView;
